import { Tutor } from "src/services/tutorsService";
import { bookingAvailabilityService, BookingTimeSlot } from "src/services/bookingAvailabilityService";

// Get day of week from date
export const getDayOfWeek = (date: Date): string => {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return days[date.getDay()];
};

// Get available days for a tutor
export const getTutorAvailableDays = (tutor: Tutor): string[] => {
  if (!tutor.availability_slots || tutor.availability_slots.length === 0) {
    return [];
  }
  
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return Array.from(
    new Set(tutor.availability_slots.map(slot => dayNames[slot.day_of_week]))
  );
};

// Check if a date is available for a tutor
export const isDateAvailable = (date: Date, tutor: Tutor): boolean => {
  const availableDays = getTutorAvailableDays(tutor);
  return availableDays.includes(getDayOfWeek(date));
};

// Enhanced function to get available time slots for booking with timezone conversion and session blocking
// This uses the correct approach: Subtract/Block Unavailable Periods to get the available window
export const getAvailableTimeSlots = async (
  date: Date, 
  tutor: Tutor, 
  targetTimezone: string,
  sessionDurationMinutes: number = 60
): Promise<string[]> => {
  try {
    // Use the booking availability service that follows the correct approach
    const slots = await bookingAvailabilityService.getAvailableBookingSlots(
      tutor.id,
      date,
      targetTimezone,
      sessionDurationMinutes
    );
    
    return bookingAvailabilityService.formatBookingSlotsForDisplay(slots);
  } catch (error) {
    console.error('Error getting available booking slots:', error);
    return [];
  }
};

// Legacy function for backward compatibility (synchronous, basic availability)
export const getBasicAvailableTimeSlots = (date: Date, tutor: Tutor): string[] => {
  if (!isDateAvailable(date, tutor)) {
    return [];
  }
  
  const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
  
  // Get tutor's availability slots for the specific day
  const daySlots = tutor.availability_slots?.filter(slot => slot.day_of_week === dayOfWeek) || [];
  
  if (daySlots.length === 0) {
    return [];
  }
  
  // Convert availability slots to time strings
  const availableSlots: string[] = [];
  
  daySlots.forEach(slot => {
    // Parse start and end times
    const startTime = slot.start_time; // e.g., "09:00:00"
    const endTime = slot.end_time;     // e.g., "17:00:00"
    
    // Generate hourly slots between start and end time
    const slots = generateTimeSlots(startTime, endTime);
    availableSlots.push(...slots);
  });
  
  // Remove duplicates and sort
  const uniqueSlots = Array.from(new Set(availableSlots));
  return uniqueSlots.sort((a, b) => {
    const timeA = convertTo24Hour(a);
    const timeB = convertTo24Hour(b);
    return timeA.localeCompare(timeB);
  });
};

// Helper function to generate time slots between start and end time
const generateTimeSlots = (startTime: string, endTime: string): string[] => {
  const slots: string[] = [];
  
  // Parse time strings (format: "HH:MM:SS")
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  // Create Date objects for easier manipulation
  const start = new Date();
  start.setHours(startHour, startMin, 0, 0);
  
  const end = new Date();
  end.setHours(endHour, endMin, 0, 0);
  
  // Generate slots every hour
  const current = new Date(start);
  while (current < end) {
    const timeString = formatTimeSlot(current);
    slots.push(timeString);
    
    // Add 1 hour
    current.setHours(current.getHours() + 1);
  }
  
  return slots;
};

// Helper function to format time slot for display
const formatTimeSlot = (date: Date): string => {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Helper function to convert 12-hour format to 24-hour for sorting
const convertTo24Hour = (time12h: string): string => {
  const [time, modifier] = time12h.split(' ');
  let [hours, minutes] = time.split(':');
  
  if (hours === '12') {
    hours = '00';
  }
  
  if (modifier === 'PM') {
    hours = String(parseInt(hours, 10) + 12);
  }
  
  return `${hours.padStart(2, '0')}:${minutes}`;
};

// Format date for display
export const formatDateForDisplay = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Get booking time slot data for a specific time string
export const getBookingTimeSlot = async (
  date: Date,
  tutor: Tutor,
  targetTimezone: string,
  timeString: string,
  sessionDurationMinutes: number = 60
): Promise<BookingTimeSlot | null> => {
  try {
    const slots = await bookingAvailabilityService.getAvailableBookingSlots(
      tutor.id,
      date,
      targetTimezone,
      sessionDurationMinutes
    );
    
    return bookingAvailabilityService.getBookingSlotByTimeString(slots, timeString);
  } catch (error) {
    console.error('Error getting booking time slot:', error);
    return null;
  }
};